import React from 'react';
import { formatWorkshiftLabel } from './workshiftLabel';
import { Box, MenuItem, TextField, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { WEEKDAY_OPTIONS, CYCLE_WEEK_ALL, CYCLE_WEEK_ODD, CYCLE_WEEK_EVEN } from './weeklyScheduleUtils';

const SCOPE_AUTO = 'auto';
const SCOPE_ALL = 'all';
const SCOPE_ALTERNATE = 'alternate';

const emptyRow = (weekday, cycle_week) => ({ weekday, cycle_week, workshift_id: '', is_free_day: false });

const turnoFieldSx = { minWidth: 180, flex: '1 1 180px' };

// Nivel 1 (¿el día tiene regla fija?): pill sólida en el navy de marca,
// mismo lenguaje que "Modalidad de asignación".
const getDayToggleSx = (theme) => ({
  backgroundColor: theme.palette.primary.light,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  borderRadius: 1.5,
  p: 0.4,
  '& .MuiToggleButtonGroup-grouped': { border: '0 !important', margin: 0 },
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.78rem',
    color: alpha(theme.palette.primary.main, 0.65),
    borderRadius: '6px !important',
    px: 1.25,
    py: 0.25,
    minHeight: 28,
  },
  '& .Mui-selected, & .Mui-selected:hover': {
    color: '#FFFFFF !important',
    backgroundColor: `${theme.palette.primary.main} !important`,
  },
});

// Nivel 2 (solo si es "Horario fijo"): un paso subordinado -- outline liviano,
// seleccionado en verde de marca, para que se lea como un detalle del nivel 1.
const getWeekToggleSx = (theme) => ({
  gap: 0.5,
  '& .MuiToggleButtonGroup-grouped': {
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)} !important`,
    borderRadius: '6px !important',
    margin: 0,
  },
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.76rem',
    color: alpha(theme.palette.primary.main, 0.7),
    backgroundColor: '#FFFFFF',
    px: 1.1,
    py: 0.2,
    minHeight: 26,
  },
  '& .Mui-selected, & .Mui-selected:hover': {
    color: `${theme.palette.primary.main} !important`,
    backgroundColor: `${alpha(theme.palette.secondary.main, 0.25)} !important`,
    borderColor: `${theme.palette.secondary.main} !important`,
  },
});

const WeeklyScheduleEditor = ({
  value = [],
  onChange,
  workshiftOptions = [],
  disabled = false,
  error = '',
  helperText = '',
}) => {
  const theme = useTheme();
  const dayToggleSx = getDayToggleSx(theme);
  const weekToggleSx = getWeekToggleSx(theme);

  const rowsByWeekday = (weekday) => (value || []).filter((r) => Number(r.weekday) === weekday);

  const updateDayRows = (weekday, nextRowsForDay) => {
    const rest = (value || []).filter((r) => Number(r.weekday) !== weekday);
    onChange([...rest, ...nextRowsForDay]);
  };

  const handleScopeChange = (weekday, scope) => {
    if (scope === SCOPE_AUTO) {
      updateDayRows(weekday, []);
    } else if (scope === SCOPE_ALL) {
      updateDayRows(weekday, [emptyRow(weekday, CYCLE_WEEK_ALL)]);
    } else {
      updateDayRows(weekday, [emptyRow(weekday, CYCLE_WEEK_ODD), emptyRow(weekday, CYCLE_WEEK_EVEN)]);
    }
  };

  // 'auto' = esa semana puntual (impar o par) sigue la rotación automática --
  // se saca la fila en vez de guardarla a medias. '' solo se usa en el select
  // "Todas las semanas", donde no hay opción automática: es un placeholder
  // mientras el usuario todavía no eligió turno/libre.
  const handleRowValueChange = (weekday, cycleWeek, nextValue) => {
    if (nextValue === 'auto') {
      updateDayRows(
        weekday,
        rowsByWeekday(weekday).filter((row) => Number(row.cycle_week) !== cycleWeek)
      );
      return;
    }

    const updatedRow =
      nextValue === ''
        ? emptyRow(weekday, cycleWeek)
        : {
            weekday,
            cycle_week: cycleWeek,
            ...(nextValue === 'free'
              ? { is_free_day: true, workshift_id: '' }
              : { is_free_day: false, workshift_id: nextValue }),
          };
    const existing = rowsByWeekday(weekday);
    const already = existing.some((row) => Number(row.cycle_week) === cycleWeek);
    updateDayRows(
      weekday,
      already
        ? existing.map((row) => (Number(row.cycle_week) !== cycleWeek ? row : updatedRow))
        : [...existing, updatedRow]
    );
  };

  const renderTurnoSelect = (weekday, cycleWeek, row, label, { allowAuto = false } = {}) => {
    const selectValue = row.is_free_day
      ? 'free'
      : row.workshift_id
      ? String(row.workshift_id)
      : (allowAuto ? 'auto' : '');
    return (
      <TextField
        key={`${weekday}-${cycleWeek}`}
        select
        size="small"
        sx={turnoFieldSx}
        disabled={disabled}
        label={label}
        value={selectValue}
        onChange={(event) => handleRowValueChange(weekday, cycleWeek, event.target.value)}
      >
        {allowAuto ? (
          <MenuItem value="auto">Rota automático</MenuItem>
        ) : (
          <MenuItem value="">
            <em>Elegí turno o libre…</em>
          </MenuItem>
        )}
        <MenuItem value="free">Libre</MenuItem>
        {workshiftOptions.map((workshift) => (
          <MenuItem key={workshift.id} value={String(workshift.id)}>
            {formatWorkshiftLabel(workshift)}
          </MenuItem>
        ))}
      </TextField>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
        Por defecto, cada día rota según los turnos de la estación. Marcá "Horario fijo" solo en
        los días con una regla fija (turno o día libre), y elegí si aplica todas las semanas o
        alterna semana por medio (ej. libre cada dos semanas).
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {WEEKDAY_OPTIONS.map(({ weekday, label }) => {
          const dayRows = rowsByWeekday(weekday);
          const allRow = dayRows.find((r) => Number(r.cycle_week) === CYCLE_WEEK_ALL);
          const oddRow = dayRows.find((r) => Number(r.cycle_week) === CYCLE_WEEK_ODD);
          const evenRow = dayRows.find((r) => Number(r.cycle_week) === CYCLE_WEEK_EVEN);
          const scope = allRow ? SCOPE_ALL : (oddRow || evenRow) ? SCOPE_ALTERNATE : SCOPE_AUTO;
          const isFixed = scope !== SCOPE_AUTO;

          return (
            <Box
              key={weekday}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 1.25,
                borderRadius: 1.5,
                backgroundColor: isFixed ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: theme.palette.primary.main, minWidth: 76 }}
                >
                  {label}
                </Typography>

                <ToggleButtonGroup
                  exclusive
                  size="small"
                  disabled={disabled}
                  value={isFixed ? 'fixed' : SCOPE_AUTO}
                  onChange={(_, next) => {
                    if (!next) return;
                    handleScopeChange(weekday, next === SCOPE_AUTO ? SCOPE_AUTO : SCOPE_ALL);
                  }}
                  sx={dayToggleSx}
                >
                  <ToggleButton value={SCOPE_AUTO}>Rota automático</ToggleButton>
                  <ToggleButton value="fixed">Horario fijo</ToggleButton>
                </ToggleButtonGroup>

                {isFixed && (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    disabled={disabled}
                    value={scope}
                    onChange={(_, next) => next && handleScopeChange(weekday, next)}
                    sx={weekToggleSx}
                  >
                    <ToggleButton value={SCOPE_ALL}>Todas las semanas</ToggleButton>
                    <ToggleButton value={SCOPE_ALTERNATE}>Alterna semana por medio</ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Box>

              {scope === SCOPE_ALL && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {renderTurnoSelect(weekday, CYCLE_WEEK_ALL, allRow || emptyRow(weekday, CYCLE_WEEK_ALL), 'Turno o libre')}
                </Box>
              )}

              {scope === SCOPE_ALTERNATE && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {renderTurnoSelect(
                    weekday,
                    CYCLE_WEEK_ODD,
                    oddRow || emptyRow(weekday, CYCLE_WEEK_ODD),
                    'Semanas impares',
                    { allowAuto: true }
                  )}
                  {renderTurnoSelect(
                    weekday,
                    CYCLE_WEEK_EVEN,
                    evenRow || emptyRow(weekday, CYCLE_WEEK_EVEN),
                    'Semanas pares',
                    { allowAuto: true }
                  )}
                </Box>
              )}

              {weekday === 6 && (error || helperText) && (
                <Typography variant="caption" sx={{ color: error ? 'error.main' : 'text.secondary' }}>
                  {error || helperText}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default WeeklyScheduleEditor;
